import { useFarmCreateMutation } from 'app/agriculture/mutations/farms/useFarmCreateMutation';
import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { Select } from 'app/agriculture/forms/components/Select/Select';
import { FarmLocationInput } from 'app/agriculture/routes/BackOffice/FarmCreate/components/FarmLocationInput';
import './FarmCreateForm.scss';
import { PointGeometry } from 'app/agriculture/queries/types/geometry/PointGeometry';
import tz_lookup from 'tz-lookup';
import { useViewerContext } from 'app/agriculture/contexts/ViewerContext/useViewerContext';
import { getViewerPosition } from 'app/agriculture/routes/BackOffice/FarmCreate/components/getViewerPosition';
import { useTimeZoneOptions } from 'app/agriculture/routes/BackOffice/FarmCreate/components/useTimeZoneOptions';

interface CreateFarmFormFields {
    name: string;
    timeZone: string;
    center: PointGeometry;
}

export function FarmCreateForm() {
    const navigate = useNavigate();
    const { mutate, isPending } = useFarmCreateMutation();
    const { viewer } = useViewerContext();
    const timeZoneOptions = useTimeZoneOptions();
    const form = useForm<CreateFarmFormFields>({
        mode: 'all',
        defaultValues: {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        shouldFocusError: true,
    });

    useEffect(() => {
        if (!viewer) return;

        return viewer.camera.moveEnd.addEventListener(() => {
            const [longitude, latitude] = getViewerPosition(viewer).center.coordinates;

            /** This isn't a great solution for reverse lookup of timezone
             *  timeZoneOptions aggregates multiple timezones under a single value and doesn't include Etc timezones
             *  e.g. Asia/Port_Moresby won't match because in the options list it's bundled in with Asia/Guam
             */
            const newTimeZone = tz_lookup(latitude, longitude);
            if (timeZoneOptions.some(({ value }) => newTimeZone === value)) {
                form.setValue('timeZone', newTimeZone);
            }
        });
    }, [viewer, form, timeZoneOptions]);

    const onSubmit: SubmitHandler<CreateFarmFormFields> = useCallback(
        (farm) => {
            if (!viewer) throw new Error('viewer not initialised');

            const { center } = getViewerPosition(viewer);

            mutate(
                {
                    ...farm,
                    center,
                },
                {
                    onSuccess: () => {
                        navigate('/', { replace: true });
                    },
                },
            );
        },
        [mutate, navigate, viewer],
    );

    return (
        <form className="FarmCreateForm" onSubmit={form.handleSubmit(onSubmit)}>
            <SimpleGrid columns={2}>
                <Input
                    label="Farm Name"
                    {...form.register('name', {
                        required: 'Please enter a name for your farm',
                    })}
                    errorMessage={form.formState.errors.name?.message}
                />
                <Select
                    fieldName="timeZone"
                    options={timeZoneOptions.map(({ value, label }) => ({
                        id: value,
                        name: label,
                    }))}
                    noun="Time Zone"
                    formController={form}
                />
            </SimpleGrid>
            <FarmLocationInput />
            <Actions
                sticky
                primaryAction={{
                    text: 'Create Farm',
                    isLoading: isPending,
                    type: 'submit',
                }}
                backAction={{
                    text: 'Back',
                    onClick: () => {
                        navigate(-1);
                    },
                }}
            />
        </form>
    );
}
